import * as React from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * The one shared empty-state treatment: icon chip + heading + supporting copy
 * + optional CTA. Use this instead of a bare "No X yet." string — for a
 * brand-new org most surfaces ARE empty states, so they carry the first
 * impression.
 *
 * `variant="panel"` (default): dashed-border panel for a page/section whose
 * entire content is missing (mirrors the RegistrationListAdmin treatment this
 * generalizes). `variant="inline"`: quiet borderless version for inside an
 * existing Card.
 */
export interface EmptyStateProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  icon?: LucideIcon;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** CTA slot — usually a single <Button>. */
  action?: React.ReactNode;
  variant?: 'panel' | 'inline';
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  variant = 'panel',
  className,
  ...props
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center',
        variant === 'panel' ? 'rounded-xl border border-dashed p-12' : 'px-6 py-10',
        className,
      )}
      {...props}
    >
      {Icon ? (
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
          <Icon className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
        </div>
      ) : null}
      <h3
        className={cn(
          'mt-3 font-semibold text-foreground',
          variant === 'panel' ? 'text-lg' : 'text-sm',
        )}
      >
        {title}
      </h3>
      {description ? (
        <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
          {description}
        </p>
      ) : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}
